import { graphemes } from './text.js';
import { boxExtent, type DesignBox, type DesignLayer } from './design-area.js';

/**
 * `centered` is Zazzle's "Centered" tiling: a basic grid whose lattice is
 * anchored on the zone's centre instead of on the motif, so the repeat sits
 * symmetrically in the print area with equal part-tiles at opposite edges,
 * wherever the motif was dragged.
 */
export const DESIGNER_PATTERN_MODES = ['basic', 'half-brick', 'half-drop', 'mirror', 'centered'] as const;
export interface DesignerPattern {
  mode: typeof DESIGNER_PATTERN_MODES[number];
  gapXmm: number;
  gapYmm: number;
}
export const MAX_DESIGNER_TILES = 400;
export function parseDesignerPattern(raw: unknown): DesignerPattern {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid pattern');
  const p = raw as DesignerPattern;
  if (Object.keys(p).some(k => !['mode','gapXmm','gapYmm'].includes(k)) || !DESIGNER_PATTERN_MODES.includes(p.mode)
    || ![p.gapXmm,p.gapYmm].every(n => typeof n === 'number' && Number.isFinite(n) && n >= 0 && n <= 200)) throw new Error('Pattern gaps must be 0–200 mm');
  return { mode:p.mode, gapXmm:p.gapXmm, gapYmm:p.gapYmm };
}

/** A compact repeat expands to native layers in the zone frame. Rotated motifs
 * use their axis-aligned extent as the cell. Only intersecting tiles are emitted. */
export function designerPatternTiles(layer: DesignLayer, size: {w:number;h:number}): DesignLayer[] {
  if (!layer.pattern) return [layer];
  const p = parseDesignerPattern(layer.pattern);
  // The lattice origin: the motif itself, or for `centered` the zone's centre.
  const origin: DesignBox = p.mode === 'centered' ? { ...layer.box, x: size.w / 2, y: size.h / 2 } : layer.box;
  const e = boxExtent(origin);
  const dx=e.right-e.left+p.gapXmm, dy=e.bottom-e.top+p.gapYmm;
  if (![dx,dy,size.w,size.h].every(n => Number.isFinite(n) && n > 0)) throw new Error('Invalid pattern dimensions');
  const nx=Math.ceil(size.w/dx)+3, ny=Math.ceil(size.h/dy)+3;
  if (nx*ny > MAX_DESIGNER_TILES*4) throw new Error('Pattern is too dense. Enlarge the motif or spacing.');
  const startX=Math.floor(-e.right/dx)-1, endX=Math.ceil((size.w-e.left)/dx)+1;
  const startY=Math.floor(-e.bottom/dy)-1, endY=Math.ceil((size.h-e.top)/dy)+1;
  const out:DesignLayer[]=[];
  for(let row=startY;row<=endY;row++) for(let col=startX;col<=endX;col++) {
    const oddX=Math.abs(col%2)===1, oddY=Math.abs(row%2)===1;
    const box:DesignBox={...origin,x:origin.x+col*dx+(p.mode==='half-brick'&&oddY?dx/2:0),y:origin.y+row*dy+(p.mode==='half-drop'&&oddX?dy/2:0)};
    const b=boxExtent(box);
    if(b.right<=0||b.bottom<=0||b.left>=size.w||b.top>=size.h) continue;
    out.push({...layer,pattern:undefined,id:`${layer.id}~${col},${row}`,box,
      flipX:p.mode==='mirror'&&oddX?!layer.flipX:layer.flipX,
      flipY:p.mode==='mirror'&&oddY?!layer.flipY:layer.flipY});
    if(out.length>MAX_DESIGNER_TILES) throw new Error(`Pattern exceeds ${MAX_DESIGNER_TILES} visible tiles. Enlarge the motif or spacing.`);
  }
  return out;
}

/** Stacked upright graphemes, retaining diacritics/emoji clusters and blank words. */
export function designerVerticalText(text:string):string {
  return graphemes(text).map(s=>s==='\n'?'':s).join('\n');
}

export function validateDesignerQr(value:unknown):string {
  if(typeof value!=='string'||!value.trim()||new TextEncoder().encode(value.trim()).length>256) throw new Error('QR URL must contain at most 256 UTF-8 bytes');
  let url:URL; try { url=new URL(value.trim()); } catch { throw new Error('Enter a complete https:// URL'); }
  if(!['https:','http:'].includes(url.protocol)||url.username||url.password) throw new Error('QR requires an HTTP(S) URL without credentials');
  return value.trim();
}
/** Conservative size increases with payload; native output always retains a
 * four-module-or-larger white quiet zone. Physical scan acceptance is separate. */
export function designerQrMinMm(value:string):number {
  return Math.ceil(20+new TextEncoder().encode(value).length/16);
}
